import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { getNoLiDays } from "../../utils/noliDays";
import AccountRecovery from './AccountRecovery';

const NoLiLayout = ({
  isAdmin,
  onToggleNoLi,
  viewAsUser,
  setViewAsUser,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [noliDays, setNoLiDays] = useState(() => getNoLiDays());
  const [storageError, setStorageError] = useState('');
  const mainContentRef = useRef(null);

  useEffect(() => {
    const failed = event => setStorageError(String(event.detail || 'Changes could not be saved. Reload before editing.'));
    window.addEventListener('noli-storage-error', failed);
    return () => window.removeEventListener('noli-storage-error', failed);
  }, []);

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "NoLi";
    document.body.classList.add("noli-health-theme");
    return () => {
      document.title = previousTitle;
      document.body.classList.remove("noli-health-theme");
    };
  }, []);

  useEffect(() => {
    const refreshDays = () => setNoLiDays(getNoLiDays());
    const interval = window.setInterval(refreshDays, 60 * 1000);
    return () => window.clearInterval(interval);
  }, []);

  const resetScrollPosition = useCallback(() => {
    mainContentRef.current?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setMobileMenuOpen(false);
      resetScrollPosition();
    }
  }, [isMobile, resetScrollPosition]);

  useEffect(() => {
    setMobileMenuOpen(false);
    resetScrollPosition();
    const raf = window.requestAnimationFrame(() => {
      resetScrollPosition();
    });
    return () => window.cancelAnimationFrame(raf);
  }, [location.pathname, resetScrollPosition]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const currentApp = location.pathname.includes("/fitness")
    ? "fitness"
    : location.pathname.includes("/life") || location.pathname.includes("/notes")
    ? "life"
    : location.pathname.includes("/timelines")
    ? "timelines"
    : location.pathname.includes("/points")
    ? "points"
    : "nutrition";

  const handleMobileNavigate = (path) => {
    setMobileMenuOpen(false);
    resetScrollPosition();
    navigate(path);
    window.requestAnimationFrame(() => {
      resetScrollPosition();
      window.requestAnimationFrame(() => {
        resetScrollPosition();
      });
    });
  };

  return (
    <div className="noli-health-theme min-h-screen bg-[#1a1b26] flex flex-col">
      <AccountRecovery />
      {storageError && <div role="alert" className="fixed inset-x-3 top-16 z-[100] mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-xl border border-[#f7768e]/50 bg-[#1a1b26] p-4 text-sm text-[#c0caf5] shadow-xl"><p>{storageError}</p><button type="button" onClick={() => window.location.reload()} className="rounded-lg bg-[#414868] px-3 py-2 text-[#c0caf5]">Reload NoLi</button></div>}
      {/* Mobile Header */}
      {isMobile && (
        <div className="z-40 bg-[#1a1b26] border-b border-[#414868]">
          {/* Top bar with logo and menu */}
          <div className="h-12 flex items-center justify-between px-4">
            <div className="flex items-center gap-2.5">
              <div className="text-[#7aa2f7] font-bold text-lg flex items-center">
                <span className="bg-[#7aa2f7] rounded-md px-2 py-0.5 mr-2"></span>
                NoLi
              </div>
              <span className="rounded-full border border-[#73daca]/40 bg-[#73daca]/10 px-2 py-0.5 text-xs font-semibold tabular-nums text-[#73daca]" title="Days since January 12, 2026">
                Days {noliDays}
              </span>
            </div>
            <button
              aria-label="Toggle navigation menu"
              aria-expanded={mobileMenuOpen}
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-[#a9b1d6] p-2"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>
            </button>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="px-4 py-2 border-t border-[#414868] bg-[#24283b]">
              {isAdmin && !viewAsUser && (
                <button
                  onClick={() => {
                    onToggleNoLi();
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left py-2 text-[#a9b1d6] hover:text-white"
                >
                  Back to FortPixel
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => {
                    setViewAsUser(!viewAsUser);
                    setMobileMenuOpen(false);
                  }}
                  className="w-full text-left py-2 text-[#a9b1d6] hover:text-white"
                >
                  {!viewAsUser ? "View as User" : "View as Admin"}
                </button>
              )}
              <button
                onClick={() => {
                  handleLogout();
                  setMobileMenuOpen(false);
                }}
                className="w-full rounded text-left py-2 text-sm font-medium text-[#a9b1d6] hover:text-[#f7768e] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7aa2f7]"
              >
                Logout
              </button>
            </div>
          )}

          {/* App Switcher - Segmented Control */}
          <div className="flex overflow-x-auto px-4 py-2">
            <button
              className={`flex-1 py-2 text-center text-sm font-medium rounded-l-lg transition-colors ${
                currentApp === "nutrition"
                  ? "bg-[#7aa2f7] text-white"
                  : "bg-[#414868] text-[#a9b1d6]"
              } border-r border-[#565f89]`}
              onClick={() => handleMobileNavigate("/dashboard/noli/nutrition")}
            >
              Nutrition
            </button>
            <button
              className={`flex-1 py-2 text-center text-sm font-medium transition-colors border-r border-[#565f89] ${
                currentApp === "fitness"
                  ? "bg-[#7aa2f7] text-white"
                  : "bg-[#414868] text-[#a9b1d6]"
              }`}
              onClick={() => handleMobileNavigate("/dashboard/noli/fitness")}
            >
              Fitness
            </button>
            <button
              className={`flex-1 py-2 text-center text-sm font-medium transition-colors border-r border-[#565f89] ${
                currentApp === "life"
                  ? "bg-[#7aa2f7] text-white"
                  : "bg-[#414868] text-[#a9b1d6]"
              }`}
              onClick={() => handleMobileNavigate("/dashboard/noli/life")}
            >
              Life
            </button>
            <button
              className={`flex-1 px-2 py-2 text-center text-sm font-medium border-r border-[#565f89] transition-colors ${
                currentApp === "timelines" ? "bg-[#7aa2f7] text-white" : "bg-[#414868] text-[#a9b1d6]"
              }`}
              onClick={() => handleMobileNavigate("/dashboard/noli/timelines")}
            >
              Timelines
            </button>
            <button
              className={`flex-1 px-2 py-2 text-center text-sm font-medium rounded-r-lg transition-colors ${currentApp === "points" ? "bg-[#7aa2f7] text-white" : "bg-[#414868] text-[#a9b1d6]"}`}
              aria-current={currentApp === "points" ? "page" : undefined}
              onClick={() => handleMobileNavigate("/dashboard/noli/points")}
            >Points</button>
          </div>
        </div>
      )}

      {/* Desktop Top Navigation Bar */}
      <div className="h-12 bg-[#1a1b26] flex items-center justify-between border-b border-[#414868] px-4 hidden lg:flex">
        {/* Left side with logo and navigation */}
        <div className="flex items-center h-full flex-1">
          {/* App Logo */}
          <div className="text-[#7aa2f7] font-bold text-lg mr-3 flex items-center">
            <span className="bg-[#7aa2f7] rounded-md px-2 py-0.5 mr-2"></span>
            NoLi
          </div>

          <span className="mr-4 rounded-full border border-[#73daca]/40 bg-[#73daca]/10 px-2.5 py-1 text-xs font-semibold tabular-nums text-[#73daca]" title="Days since January 12, 2026">
            Days {noliDays}
          </span>

          {/* Navigation Links */}
          <nav className="flex-1 h-full">
            <ul className="flex space-x-1 h-full items-center">
              <li>
                <Link
                  to="/dashboard/noli/nutrition"
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 hover:bg-[#414868] ${
                    location.pathname === "/dashboard/noli/nutrition"
                      ? "bg-[#7aa2f7] text-[#1a1b26] shadow-sm"
                      : "text-[#a9b1d6] hover:text-white"
                  }`}
                >
                  Nutrition
                </Link>
              </li>
              <li>
                <Link
                  to="/dashboard/noli/fitness"
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 hover:bg-[#414868] ${
                    location.pathname === "/dashboard/noli/fitness"
                      ? "bg-[#7aa2f7] text-[#1a1b26] shadow-sm"
                      : "text-[#a9b1d6] hover:text-white"
                  }`}
                >
                  Fitness
                </Link>
              </li>
              <li>
                <Link
                  to="/dashboard/noli/life"
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 hover:bg-[#414868] ${
                    location.pathname === "/dashboard/noli/life"
                      ? "bg-[#7aa2f7] text-[#1a1b26] shadow-sm"
                      : "text-[#a9b1d6] hover:text-white"
                  }`}
                >
                  Life
                </Link>
              </li>
              <li>
                <Link
                  to="/dashboard/noli/timelines"
                  aria-current={currentApp === "timelines" ? "page" : undefined}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 hover:bg-[#414868] ${
                    currentApp === "timelines" ? "bg-[#7aa2f7] text-[#1a1b26] shadow-sm" : "text-[#a9b1d6] hover:text-white"
                  }`}
                >
                  Timelines
                </Link>
              </li>
              <li>
                <Link to="/dashboard/noli/points" aria-current={currentApp === "points" ? "page" : undefined}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-all duration-200 hover:bg-[#414868] ${currentApp === "points" ? "bg-[#7aa2f7] text-[#1a1b26] shadow-sm" : "text-[#a9b1d6] hover:text-white"}`}
                >Points</Link>
              </li>
            </ul>
          </nav>
        </div>

        {/* Right side with controls */}
        <div className="flex items-center gap-4">
          {/* Back to FortPixel Toggle */}
          {isAdmin && !viewAsUser && (
            <button
              onClick={onToggleNoLi}
              className="flex items-center gap-2 bg-[#414868] px-3 py-1.5 rounded-full shadow-lg shadow-black/20 hover:bg-[#565f89] transition-colors cursor-pointer"
            >
              <div className="w-2 h-2 rounded-full bg-[#7aa2f7]"></div>
              <span className="text-sm text-[#a9b1d6]">Back to FortPixel</span>
            </button>
          )}

          {/* Admin Toggle */}
          {isAdmin && (
            <button
              onClick={() => setViewAsUser(!viewAsUser)}
              className="flex items-center gap-2 bg-[#414868] px-3 py-1.5 rounded-full shadow-lg shadow-black/20 hover:bg-[#565f89] transition-colors cursor-pointer"
            >
              <div
                className={`w-2 h-2 rounded-full ${
                  !viewAsUser ? "bg-fortpixel-gold" : "bg-gray-500"
                }`}
              ></div>
              <span className="text-sm text-[#a9b1d6]">
                {!viewAsUser ? "Admin" : "Viewing as User"}
              </span>
            </button>
          )}

          {!isAdmin && (
            <div className="flex items-center gap-2 bg-[#414868] px-3 py-1.5 rounded-full shadow-lg shadow-black/20">
              <div className="w-2 h-2 rounded-full bg-gray-500"></div>
              <span className="text-sm text-[#a9b1d6]">User</span>
            </div>
          )}

          {/* Logout Button */}
          <button
            onClick={handleLogout}
            className="shrink-0 rounded-full bg-[#414868] px-3 py-1.5 text-sm font-medium leading-5 text-[#a9b1d6] transition-colors hover:bg-[#f7768e]/10 hover:text-[#f7768e] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7aa2f7] focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1b26]"
          >
            Logout
          </button>
        </div>
      </div>

      {/* Main Content */}
      <main
        id="noli-live-content"
        ref={mainContentRef}
        className="flex-1 overflow-auto"
      >
        <Outlet />
      </main>
    </div>
  );
};

export default NoLiLayout;
